import { createContext, useState, useRef, useEffect, useMemo, useContext } from 'react'

import { CompareItemsModes, CompareItemsProps, CompareItemsAnalysisMode } from '../types'
import { DEFAULT_ANALYSIS_MODE } from '../config/CompareItems_const'

export const ModesContext = createContext<CompareItemsModes>(null)

export default function useModes(): CompareItemsModes {
  const modes = useContext(ModesContext)
  return modes
}

export function useModesState(props: CompareItemsProps): CompareItemsModes {
  // states
  const [analysisMode, setAnalysisMode] = useState<CompareItemsAnalysisMode>(
    props.analysisMode || DEFAULT_ANALYSIS_MODE
  )
  const [fullTexts, setFullTexts] = useState<boolean>(props.fullTexts ?? false)
  const [fullScreen, setFullScreen] = useState<boolean>(false)

  // sync managed states
  const lastProps = useRef<Partial<CompareItemsProps>>()
  if (null == lastProps.current) {
    lastProps.current = {
      analysisMode: props.analysisMode,
      fullTexts: props.fullTexts
    }
  }
  useEffect(() => {
    // Update analysisMode when props.analysisMode changes
    if (lastProps.current.analysisMode !== props.analysisMode) {
      lastProps.current.analysisMode = props.analysisMode
      setAnalysisMode(props.analysisMode || DEFAULT_ANALYSIS_MODE)
    }
    // Update fullTexts when props.fullTexts changes
    if (lastProps.current.fullTexts !== props.fullTexts) {
      lastProps.current.fullTexts = props.fullTexts
      setFullTexts(props.fullTexts ?? false)
    }
  }, [props.analysisMode, props.fullTexts])

  // safe callbacks ref
  const callbackRefs = useRef<{
    onAnalysisModeUpdated?: CompareItemsProps['onAnalysisModeUpdated']
    onFullTextsUpdated?: CompareItemsProps['onFullTextsUpdated']
    onChangeSplitBy?: CompareItemsProps['onChangeSplitBy']
  }>({})
  callbackRefs.current.onAnalysisModeUpdated = props.onAnalysisModeUpdated
  callbackRefs.current.onFullTextsUpdated = props.onFullTextsUpdated
  callbackRefs.current.onChangeSplitBy = props.onChangeSplitBy

  const modes = useMemo<CompareItemsModes>(() => {
    return {
      analysisMode,
      setAnalysisMode: (mode: CompareItemsAnalysisMode) => {
        setAnalysisMode(mode)
        callbackRefs.current.onAnalysisModeUpdated?.(mode)
      },
      fullTexts,
      setFullTexts: (fullTexts: boolean) => {
        setFullTexts(fullTexts)
        callbackRefs.current.onFullTextsUpdated?.(fullTexts)
      },
      fullScreen,
      setFullScreen: (fullScreen: boolean) => {
        setFullScreen(fullScreen)
        // No callback for fullScreen, as it's typically a UI state change
      },
      splitBy: props.splitBy,
      get useSplitBy() {
        return 'function' === typeof callbackRefs.current.onChangeSplitBy
      },
      onChangeSplitBy: (splitBy: CompareItemsProps['splitBy']) => {
        callbackRefs.current.onChangeSplitBy?.(splitBy)
      }
    }
  }, [analysisMode, fullTexts, fullScreen, props.splitBy])

  return modes
}
